import {
  TextInput,
  TextInputProps,
  TextStyle,
  ViewStyle,
  ImageStyle,
  Pressable,
  View,
  Image,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native';
import React, {
  useState,
  useEffect,
  useMemo,
  useLayoutEffect,
  useCallback,
} from 'react';
import {corner_radius, spacing} from 'theme/Units';
import colors from 'theme/Colors';
import {mergeDeepRight} from 'ramda';
import {Calendar} from './Calender';
import {getFormattedDate} from 'utils/getFormattedDate';
import {Text} from 'ui-kits';

type sizeTypes = 'lg' | 'md' | 'sm';
type inputStateTypes = 'default' | 'disabled' | 'error' | 'focus';

type propTypes = Readonly<
  {
    label: string;
    helperLabel?: string;
    optionalLabel?: string;
    size?: sizeTypes;
    disabled?: boolean;
    error?: string;
    required?: boolean;
    width?: number;
    value?: Date | null;
    zIndex?: number;
    onChangeDate?: (data: Date | null) => void;
    onBlur?: (date: Date | null) => void;
    calenderStyle?: ViewStyle;
  } & Omit<TextInputProps, 'value' | 'onBlur'>
>;

export function DatePicker(props: propTypes) {
  const {
    width,
    label,
    helperLabel,
    optionalLabel,
    error,
    onChangeDate,
    onFocus,
    onBlur,
    required,
    size = 'md',
    disabled,
    value,
    calenderStyle,
  } = props;
  const [date, setDate] = useState<Date | null>(value ?? null);

  const [inputState, setInputState] = useState<inputStateTypes>(
    disabled ? 'disabled' : 'default',
  );
  const [showCalender, setShowCalender] = useState<boolean>(false);
  const formattedData = useMemo<string>(() => {
    if (date) {
      return getFormattedDate(date, 'dd-mm-yyyy', ' / ');
    }
    return '';
  }, [date]);
  /**
   * overriding styles based on size variants as well as input states
   */

  const updateInputState = useCallback(
    (state: inputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  function handleDateSelect(selectedDate: Date) {
    setDate(selectedDate);
    updateInputState('default');
    onChangeDate?.(selectedDate);
  }

  useLayoutEffect(() => {
    if (value) {
      setDate(value);
    }
  }, [value]);

  const styles = mergeDeepRight(
    mergeDeepRight(baseStyles, inputStateStyle[inputState]),
    sizeStyles[size],
  ) as styleTypes;

  const calenderIcon = (
    <Pressable
      onPress={() => {
        updateInputState('focus');
        setShowCalender(prev => !prev);
      }}>
      <Image
        source={require('assets/icons/calender@2x.png')}
        style={styles.icon}
      />
    </Pressable>
  );

  function onFocusHandler(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ): void {
    updateInputState('focus');
    setShowCalender(true);
    onFocus?.(e);
  }

  function onBlurHanlder(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ): void {
    updateInputState(error ? 'error' : 'default');
    if (date) {
      onBlur?.(date);
    } else {
      onBlur?.(null);
    }
  }

  function closeDropDown() {
    updateInputState('default');
    setShowCalender(false);
  }

  useEffect(() => {
    if (error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, updateInputState]);

  return (
    <>
      <View style={[styles.inputContainer, {maxWidth: width}]}>
        {/* label */}
        <View style={styles.labelWrapper}>
          <Text style={styles.label}>
            {label}{' '}
            {required ? <Text style={styles.redAsterisk}>*</Text> : null}
          </Text>
          <Text style={styles.optionnalLabel}>{optionalLabel}</Text>
        </View>

        {/* input wrapper */}
        <View style={styles.inputWrapper}>
          <TextInput
            keyboardType={'number-pad'}
            placeholder="DD / MM / YYYY"
            value={formattedData}
            onFocus={onFocusHandler}
            onBlur={onBlurHanlder}
            style={styles.input}
          />
          {/* calander icons */}
          {calenderIcon}
        </View>

        {/* helper */}
        {helperLabel ? (
          <Text style={styles.helperLabel}>{helperLabel}</Text>
        ) : null}
        {error ? <Text style={styles.errorLabel}>{error}</Text> : null}
      </View>
      {!disabled && showCalender ? (
        <Calendar
          style={calenderStyle}
          value={date}
          onChangeDate={handleDateSelect}
          onClose={closeDropDown}
        />
      ) : null}
    </>
  );
}

type styleTypes = {
  inputContainer: ViewStyle;
  icon: ImageStyle;
  label: TextStyle;
  helperLabel: TextStyle;
  optionnalLabel: TextStyle;
  errorLabel: TextStyle;
  inputWrapper: ViewStyle;
  calenderWrapper: ViewStyle;
  redAsterisk: TextStyle;
  labelWrapper: ViewStyle;
  input: TextStyle;
};
const baseStyles: styleTypes = {
  input: {
    paddingVertical: spacing[2],
    minWidth: 0,
    borderStyle: 'solid',
    outlineColor: 'transparent',
    justifyContent: 'center',
    flexDirection: 'row',
    paddingHorizontal: spacing['1'],
    height: '100%',
    flex: 1,
    fontWeight: 500,
    color: colors.palette.black,
  },
  labelWrapper: {flexDirection: 'row', justifyContent: 'space-between'},
  redAsterisk: {color: colors.palette.errorRedDark},
  inputContainer: {
    width: '100%',
    backgroundColor: colors.palette.white,
    borderRadius: corner_radius.rounded,
    gap: spacing['2'],
    position: 'relative',
  },
  label: {
    color: colors.palette.black,
    fontWeight: '600',
    fontSize: 16,
  },
  helperLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '400',
  },
  optionnalLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '500',
  },
  errorLabel: {
    color: colors.palette.errorRedDark,
    fontSize: 12,
    fontWeight: '400',
  },
  inputWrapper: {
    flexDirection: 'row',
    backgroundColor: colors.palette.white,
    borderWidth: 2,
    alignItems: 'center',
    gap: spacing['2'],
    borderRadius: corner_radius.rounded,
    paddingHorizontal: spacing['2'],
    paddingVertical: spacing['1'],
    borderStyle: 'solid',
  },
  icon: {
    width: 14,
    height: 14,
    resizeMode: 'contain',
  },
  calenderWrapper: {
    position: 'absolute',
    zIndex: 999,
    top: 30,
    left: 0,
    right: 0,
    boxShadow: '',
  },
};

const inputStateStyle: Record<inputStateTypes, Partial<styleTypes>> = {
  default: {
    inputWrapper: {
      borderColor: colors.palette.stroke,
    },
  },
  disabled: {
    inputWrapper: {
      backgroundColor: 'rgba(160, 160, 160, 0.2)',
      borderColor: colors.palette.stroke,
    },
  },
  error: {
    inputWrapper: {
      borderColor: colors.palette.errorRedDark,
    },
    label: {
      color: colors.palette.errorRedDark,
    },
  },
  focus: {
    inputWrapper: {
      borderColor: 'rgba(158, 119, 237, 0.4)',
      borderWidth: 3,
    },
  },
};

const sizeStyles: Record<sizeTypes, Partial<styleTypes>> = {
  lg: {
    inputWrapper: {
      height: 48,
      fontSize: 18,
    },
  },
  md: {
    inputWrapper: {
      height: 40,
      fontSize: 14,
    },
  },
  sm: {
    inputWrapper: {
      height: 32,
      fontSize: 14,
    },
  },
};
